import type {
  BraceletPatternDto,
  CellValue,
  NewPatternInput,
  PatternRow,
  PaletteDto,
} from '@miyuki/types';

import { ValidationError } from '../errors';
import type { Clock } from '../shared/clock';
import type { IdGenerator } from '../shared/id';

import { defaultPalette } from './palette';
import {
  DEFAULT_PATTERN_TYPE,
  DEFAULT_PROGRESS,
  DEFAULT_READING_ALTERNATES,
  MAX_COLORS,
  MAX_COLUMNS,
  MAX_NAME_LENGTH,
  MAX_ROWS,
  MIN_COLORS,
  MIN_COLUMNS,
  MIN_ROWS,
} from './pattern.constants';
import { assertPatternType } from './pattern.type';

/**
 * Parche que revierte una operación.
 *
 * La edición de celdas guarda solo el valor anterior de cada celda tocada —tres
 * números— porque es la operación que más se repite. Los cambios estructurales
 * y de paleta son raros y guardan el patrón anterior completo, que comparte en
 * memoria todas las filas que no cambiaron. Gracias a eso el historial no
 * necesita tope.
 */
export type PatternPatch =
  | { readonly kind: 'cells'; readonly cells: readonly CellPatch[] }
  | {
      readonly kind: 'snapshot';
      readonly pattern: BraceletPatternDto;
      /**
       * Cuánto mueve el avance aplicar este parche. Es cero salvo en las
       * operaciones que agregan o quitan filas, y va en el parche —y no en el
       * patrón fotografiado— porque el snapshot restaura la **cuadrícula**: el
       * estado de tejido se arrastra vivo (data-model §7).
       *
       * El campo es requerido a propósito. Así el typecheck obliga a decidirlo
       * en cada productor de parches en vez de dejar que alguno herede un cero
       * por omisión sin haberlo pensado.
       */
      readonly progressDelta: number;
    };

export interface CellPatch {
  readonly row: number;
  readonly column: number;
  readonly value: CellValue;
}

/** Resultado de toda operación que modifica el patrón. */
export interface PatternEdit {
  readonly pattern: BraceletPatternDto;
  readonly undo: PatternPatch;
}

/** Parche vacío: la operación no cambió nada y no debe ensuciar el historial. */
export const NO_CHANGES: PatternPatch = { kind: 'cells', cells: [] };

export function isNoChange(patch: PatternPatch): boolean {
  return patch.kind === 'cells' && patch.cells.length === 0;
}

function fail(message: string, path: string, received: unknown): never {
  throw new ValidationError(message, [{ path, message: `Recibido: ${String(received)}` }]);
}

function assertIntegerInRange(
  value: number,
  min: number,
  max: number,
  path: string,
  label: string,
): void {
  if (!Number.isInteger(value) || value < min || value > max) {
    fail(`${label} debe ser un entero entre ${min} y ${max}`, path, value);
  }
}

/** Nombre recortado y dentro de 1..60 caracteres (I6). */
export function normalizeName(name: string): string {
  const trimmed = name.trim();
  if (trimmed.length < 1 || trimmed.length > MAX_NAME_LENGTH) {
    fail(`El nombre debe tener entre 1 y ${MAX_NAME_LENGTH} caracteres`, 'name', name);
  }
  return trimmed;
}

/** Fila vacía del ancho indicado. */
export function emptyRow(columns: number): PatternRow {
  return Array.from({ length: columns }, () => null);
}

/**
 * Comprueba las siete invariantes del agregado (modelo de datos §2). Toda
 * operación la usa como red de seguridad y el adaptador de almacenamiento la
 * aplica a lo que lee, porque un dato corrupto puede parecer válido de forma.
 */
export function assertPatternInvariants(pattern: BraceletPatternDto): void {
  // I1
  assertIntegerInRange(pattern.columns, MIN_COLUMNS, MAX_COLUMNS, 'columns', 'El ancho');

  // I2
  assertIntegerInRange(pattern.rows.length, MIN_ROWS, MAX_ROWS, 'rows', 'La cantidad de filas');

  // I5 — antes que I4, porque validar las celdas exige una paleta creíble.
  assertPaletteInvariants(pattern.palette);

  const colorCount = pattern.palette.colors.length;
  pattern.rows.forEach((row, rowIndex) => {
    // I3
    if (row.length !== pattern.columns) {
      fail(
        `La fila ${rowIndex + 1} tiene ${row.length} celdas y el diseño declara ${pattern.columns}`,
        `rows.${rowIndex}`,
        row.length,
      );
    }

    // I4
    row.forEach((cell, columnIndex) => {
      if (cell === null) return;
      if (!Number.isInteger(cell) || cell < 0 || cell >= colorCount) {
        fail(
          `La celda (${rowIndex + 1}, ${columnIndex + 1}) apunta a un color que no existe`,
          `rows.${rowIndex}.${columnIndex}`,
          cell,
        );
      }
    });
  });

  // I6
  normalizeName(pattern.name);

  // I7
  assertPatternType(pattern.patternType);

  // I8 — el tope es la cantidad de filas, no una constante: `rows.length`
  // significa terminado y es un valor legítimo (RF-12, RF-68).
  assertIntegerInRange(pattern.progress, 0, pattern.rows.length, 'progress', 'El avance');

  // I9
  if (typeof pattern.readingAlternates !== 'boolean') {
    fail(
      'La alternancia del sentido de lectura debe ser booleana',
      'readingAlternates',
      pattern.readingAlternates,
    );
  }
}

/** Invariante I5, por separado: las operaciones de paleta la reutilizan. */
export function assertPaletteInvariants(palette: PaletteDto): void {
  assertIntegerInRange(
    palette.declaredCount,
    MIN_COLORS,
    MAX_COLORS,
    'palette.declaredCount',
    'El tope de colores',
  );

  if (palette.colors.length < MIN_COLORS) {
    fail('La paleta debe tener al menos un color', 'palette.colors', palette.colors.length);
  }

  if (palette.colors.length > palette.declaredCount) {
    fail(
      `La paleta tiene ${palette.colors.length} colores y se declararon ${palette.declaredCount}`,
      'palette.colors',
      palette.colors.length,
    );
  }
}

/**
 * Crea un patrón nuevo, todo vacío, con la paleta sembrada.
 *
 * `name`, `rowCount` y `colorCount` los aporta la aplicación: el nombre
 * provisional es texto de interfaz y depende del idioma, y la cantidad de filas
 * iniciales depende de la altura visible del lienzo. El dominio solo los valida.
 */
export function createPattern(
  input: NewPatternInput,
  clock: Clock,
  ids: IdGenerator,
): BraceletPatternDto {
  const name = normalizeName(input.name);
  assertIntegerInRange(input.columns, MIN_COLUMNS, MAX_COLUMNS, 'columns', 'El ancho');
  assertIntegerInRange(input.rowCount, MIN_ROWS, MAX_ROWS, 'rowCount', 'La cantidad de filas');
  assertIntegerInRange(
    input.colorCount,
    MIN_COLORS,
    MAX_COLORS,
    'colorCount',
    'La cantidad de colores',
  );

  // Omitirlo es lo normal: quien no elige tejido quiere una cuadrícula recta.
  const patternType = input.patternType ?? DEFAULT_PATTERN_TYPE;
  assertPatternType(patternType);

  // Una sola marca de tiempo: createdAt y updatedAt arrancan iguales.
  const timestamp = clock.now();

  return {
    id: ids.next(),
    name,
    columns: input.columns,
    rows: Array.from({ length: input.rowCount }, () => emptyRow(input.columns)),
    palette: {
      colors: defaultPalette(input.colorCount),
      declaredCount: input.colorCount,
    },
    patternType,
    progress: DEFAULT_PROGRESS,
    readingAlternates: DEFAULT_READING_ALTERNATES,
    createdAt: timestamp,
    updatedAt: timestamp,
  };
}

/**
 * `updatedAt` es la fecha del último **guardado**, no de la última pulsación:
 * las operaciones no lo tocan y por eso no reciben un `Clock`. Quien persiste
 * el patrón lo sella con esta función.
 */
export function stampUpdatedAt(pattern: BraceletPatternDto, clock: Clock): BraceletPatternDto {
  return { ...pattern, updatedAt: clock.now() };
}

/** Devuelve el patrón con una sola celda cambiada, compartiendo las demás filas. */
export function withCell(
  pattern: BraceletPatternDto,
  row: number,
  column: number,
  value: CellValue,
): BraceletPatternDto {
  const rows = pattern.rows.map((current, index) => {
    if (index !== row) return current;
    const next = [...current];
    next[column] = value;
    return next;
  });

  return { ...pattern, rows };
}

/**
 * Aplica un parche y devuelve el parche que lo revierte. Deshacer y rehacer
 * son la misma operación en sentidos opuestos, así que comparten esta función.
 */
export function applyPatch(pattern: BraceletPatternDto, patch: PatternPatch): PatternEdit {
  if (patch.kind === 'snapshot') {
    // El avance y la alternancia son del tejido en curso, no de la cuadrícula:
    // sobreviven al snapshot y solo se corrigen por el delta. Es lo que hace
    // que deshacer una edición no desteja nada (RF-47, RF-67).
    const rowCount = patch.pattern.rows.length;
    const progress = Math.min(Math.max(pattern.progress + patch.progressDelta, 0), rowCount);

    return {
      pattern: {
        ...patch.pattern,
        progress,
        readingAlternates: pattern.readingAlternates,
      },
      undo: { kind: 'snapshot', pattern, progressDelta: -patch.progressDelta },
    };
  }

  const inverse: CellPatch[] = [];
  const next = patch.cells.reduce((current, cell) => {
    inverse.push({
      row: cell.row,
      column: cell.column,
      value: current.rows[cell.row]?.[cell.column] ?? null,
    });
    return withCell(current, cell.row, cell.column, cell.value);
  }, pattern);

  return { pattern: next, undo: { kind: 'cells', cells: inverse } };
}

/** Comprueba que `index` señala una fila existente. */
export function assertRowIndex(pattern: BraceletPatternDto, index: number): void {
  if (!Number.isInteger(index) || index < 0 || index >= pattern.rows.length) {
    fail(`No existe la fila ${index + 1}`, 'row', index);
  }
}

/** Comprueba que `index` señala una columna existente. */
export function assertColumnIndex(pattern: BraceletPatternDto, index: number): void {
  if (!Number.isInteger(index) || index < 0 || index >= pattern.columns) {
    fail(`No existe la columna ${index + 1}`, 'column', index);
  }
}

/** Comprueba que `index` señala un color existente de la paleta. */
export function assertColorIndex(pattern: BraceletPatternDto, index: number): void {
  if (!Number.isInteger(index) || index < 0 || index >= pattern.palette.colors.length) {
    fail(`No existe el color ${index} en la paleta`, 'colorIndex', index);
  }
}
